<template>
  <main class="financiers-card">
    <header class="financiers-heading">
      <div>
        <div class="section-kicker">Používatelia</div>
        <h2>Finančníci</h2>
        <p>Účty, kontaktné údaje, lokality a prístupy do bankových portálov.</p>
      </div>
      <button class="primary-button" type="button" @click="startCreating">
        + Pridať finančníka
      </button>
    </header>

    <div v-if="loading" class="financiers-state">
      <v-progress-circular class="loading-spinner small" indeterminate />
      <span>Načítavam finančníkov...</span>
    </div>

    <div v-else-if="loadError" class="financiers-state error-state">
      <strong>Finančníkov sa nepodarilo načítať.</strong>
      <button class="secondary-button" type="button" @click="loadData">
        Skúsiť znova
      </button>
    </div>

    <div v-else class="financiers-workspace">
      <aside class="financiers-list">
        <div class="list-toolbar">
          <label>
            <span>Hľadať</span>
            <input
              v-model.trim="search"
              type="search"
              placeholder="Meno alebo email"
            >
          </label>
          <div class="status-filter">
            <button
              v-for="option in statusOptions"
              :key="option.value"
              type="button"
              :class="{active: statusFilter === option.value}"
              @click="statusFilter = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="list-count">
          {{ filteredFinanciers.length }} {{ financierCountLabel(filteredFinanciers.length) }}
        </div>

        <div class="financier-items">
          <button
            v-for="financier in filteredFinanciers"
            :key="financier.id"
            class="financier-item"
            :class="{active: selectedId === financier.id && !creating}"
            type="button"
            @click="selectFinancier(financier)"
          >
            <span
              class="avatar"
              :class="{inactive: !financier.is_active}"
              :style="avatarStyle(financier)"
            >
              <span v-if="!financier.profile_image">
                {{ initials(financier) }}
              </span>
            </span>
            <span class="financier-summary">
              <strong>{{ fullName(financier) }}</strong>
              <small>{{ financier.email }}</small>
              <span class="summary-meta">
                <em :class="{success: financier.is_active}">
                  {{ financier.is_active ? "Aktívny" : "Neaktívny" }}
                </em>
                <em v-if="financier.staff_profile && financier.staff_profile.is_pooled" class="pooling">
                  Prijíma leady
                </em>
              </span>
            </span>
            <span class="client-count">
              <strong>{{ financier.assigned_clients_count || 0 }}</strong>
              <small>klientov</small>
            </span>
          </button>

          <div v-if="!filteredFinanciers.length" class="empty-list">
            Žiadny finančník nezodpovedá filtru.
          </div>
        </div>
      </aside>

      <section class="financier-detail">
        <div v-if="!form" class="empty-detail">
          <span class="empty-detail-icon">👤</span>
          <strong>Vyberte finančníka</strong>
          <p>Vľavo vyberte účet, ktorý chcete skontrolovať alebo upraviť.</p>
          <button class="primary-button" type="button" @click="startCreating">
            Pridať finančníka
          </button>
        </div>

        <template v-else>
          <div class="detail-heading">
            <div class="detail-identity">
              <span
                class="avatar large"
                :style="formAvatarStyle"
              >
                <span v-if="!formAvatarUrl">{{ formInitials }}</span>
              </span>
              <div>
                <div class="detail-eyebrow">
                  {{ creating ? "Nový účet" : `Finančník #${form.id}` }}
                </div>
                <h3>{{ formDisplayName }}</h3>
                <span>{{ form.email || "Email ešte nie je vyplnený" }}</span>
              </div>
            </div>
            <button class="close-detail" type="button" title="Zavrieť" @click="closeDetail">
              ×
            </button>
          </div>

          <nav class="detail-tabs" aria-label="Údaje finančníka">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              type="button"
              :class="{active: activeTab === tab.value}"
              @click="activeTab = tab.value"
            >
              {{ tab.label }}
              <span v-if="tab.value === 'locations'">{{ selectedDistrictCount }}</span>
              <span v-if="tab.value === 'banks'">{{ configuredBankCount }}</span>
            </button>
          </nav>

          <form class="detail-form" @submit.prevent="saveFinancier">
            <section v-show="activeTab === 'account'" class="form-section">
              <div class="form-section-heading">
                <div>
                  <h4>Účet a osobné údaje</h4>
                  <p>Základné údaje používané na prihlásenie a zobrazenie v Brokeri.</p>
                </div>
              </div>

              <div class="avatar-editor">
                <span class="avatar xlarge" :style="formAvatarStyle">
                  <span v-if="!formAvatarUrl">{{ formInitials }}</span>
                </span>
                <label class="file-button">
                  <input type="file" accept="image/*" @change="selectProfileImage">
                  Nahrať profilovú fotku
                </label>
                <small>JPG, PNG alebo WebP. Fotka sa odošle až pri uložení.</small>
              </div>

              <div class="fields-grid three">
                <label class="form-field compact">
                  <span>Titul</span>
                  <input v-model.trim="form.degree" type="text" maxlength="16" placeholder="Ing.">
                </label>
                <label class="form-field">
                  <span>Meno *</span>
                  <input v-model.trim="form.first_name" type="text" required>
                </label>
                <label class="form-field">
                  <span>Priezvisko *</span>
                  <input v-model.trim="form.last_name" type="text" required>
                </label>
              </div>

              <div class="fields-grid two">
                <label class="form-field">
                  <span>Prihlasovací email *</span>
                  <input v-model.trim="form.email" type="email" required>
                </label>
                <label class="form-field">
                  <span>{{ creating ? "Úvodné heslo *" : "Nové heslo" }}</span>
                  <input
                    v-model="form.password"
                    type="password"
                    :required="creating"
                    minlength="8"
                    autocomplete="new-password"
                    :placeholder="creating ? 'Minimálne 8 znakov' : 'Nechajte prázdne bez zmeny'"
                  >
                </label>
              </div>

              <div class="switch-grid">
                <label class="setting-switch">
                  <input v-model="form.is_active" type="checkbox">
                  <span class="switch-track"><span></span></span>
                  <span>
                    <strong>Aktívny účet</strong>
                    <small>Finančník sa môže prihlásiť do aplikácie.</small>
                  </span>
                </label>
                <label class="setting-switch">
                  <input v-model="form.is_temporary" type="checkbox">
                  <span class="switch-track"><span></span></span>
                  <span>
                    <strong>Dočasný účet</strong>
                    <small>Označenie používané administráciou.</small>
                  </span>
                </label>
              </div>

              <div v-if="!creating" class="read-only-meta">
                <div>
                  <span>Vytvorený</span>
                  <strong>{{ formatDate(form.date_joined) }}</strong>
                </div>
                <div>
                  <span>Posledné prihlásenie</span>
                  <strong>{{ formatDate(form.last_login) || "Zatiaľ sa neprihlásil" }}</strong>
                </div>
                <div>
                  <span>Rola</span>
                  <strong>Finančník</strong>
                </div>
              </div>
            </section>

            <section v-show="activeTab === 'profile'" class="form-section">
              <div class="form-section-heading">
                <div>
                  <h4>Profil finančníka</h4>
                  <p>Údaje používané v dokumentoch, kontakte s klientom a automatizáciách.</p>
                </div>
              </div>

              <div class="fields-grid two">
                <label class="form-field">
                  <span>Samostatný finančný agent</span>
                  <select v-model="form.profile.sfa">
                    <option :value="null">Nevybraný</option>
                    <option v-for="sfa in metadata.sfas" :key="sfa.id" :value="sfa.id">
                      {{ sfa.name }} · {{ sfa.registration_number }}
                    </option>
                  </select>
                </label>
                <label class="form-field">
                  <span>Telefón</span>
                  <input v-model.trim="form.profile.telephone" type="tel" placeholder="+421...">
                </label>
              </div>

              <div class="fields-grid two">
                <label class="form-field">
                  <span>Číslo registrácie NBS</span>
                  <input v-model.trim="form.profile.cislo_registracie_NBS" type="text">
                </label>
                <label class="form-field">
                  <span>Obchodné meno</span>
                  <input v-model.trim="form.profile.obchodne_meno" type="text">
                </label>
              </div>

              <label class="form-field">
                <span>Adresa</span>
                <input v-model.trim="form.profile.adresa" type="text">
              </label>

              <div class="fields-grid two">
                <label class="form-field">
                  <span>IČO</span>
                  <input v-model.trim="form.profile.ico" type="text" maxlength="20">
                </label>
                <label class="form-field">
                  <span>Kontaktný email</span>
                  <input v-model.trim="form.profile.email" type="email">
                </label>
              </div>

              <label class="form-field">
                <span>Fillout ID</span>
                <input v-model.trim="form.profile.fillout_id" type="text">
              </label>

              <label class="setting-switch featured">
                <input v-model="form.profile.is_pooled" type="checkbox">
                <span class="switch-track"><span></span></span>
                <span>
                  <strong>Automatické prideľovanie leadov</strong>
                  <small>
                    Aktívny finančník môže prijímať nových klientov z vybraných lokalít.
                  </small>
                </span>
              </label>
            </section>

            <section v-show="activeTab === 'locations'" class="form-section locations-section">
              <div class="form-section-heading">
                <div>
                  <h4>Obsluhované lokality</h4>
                  <p>Okresy sa používajú pri automatickom prideľovaní nových leadov.</p>
                </div>
                <span class="selection-count">{{ selectedDistrictCount }} vybraných</span>
              </div>

              <div v-if="selectedDistricts.length" class="selected-districts">
                <button
                  v-for="district in selectedDistricts"
                  :key="district.id"
                  type="button"
                  @click="toggleDistrict(district.id)"
                >
                  {{ district.name }} <span>×</span>
                </button>
              </div>

              <label class="district-search">
                <span>Filtrovať okresy</span>
                <input
                  v-model.trim="districtSearch"
                  type="search"
                  placeholder="Napr. Bratislava, Nitra..."
                >
              </label>

              <div class="district-groups">
                <section v-for="group in districtGroups" :key="group.name">
                  <div class="district-group-heading">
                    <strong>{{ group.name || "Bez kraja" }}</strong>
                    <button type="button" @click="toggleDistrictGroup(group)">
                      {{ isDistrictGroupSelected(group) ? "Zrušiť kraj" : "Vybrať kraj" }}
                    </button>
                  </div>
                  <div class="district-options">
                    <label v-for="district in group.districts" :key="district.id">
                      <input
                        type="checkbox"
                        :checked="form.profile.districts_served.includes(district.id)"
                        @change="toggleDistrict(district.id)"
                      >
                      <span>{{ district.name }}</span>
                    </label>
                  </div>
                </section>
              </div>
            </section>

            <section v-show="activeTab === 'banks'" class="form-section banks-section">
              <div class="form-section-heading">
                <div>
                  <h4>Bankové údaje</h4>
                  <p>ID obchodníka, portálové meno a koeficient odmeny pre jednotlivé banky.</p>
                </div>
              </div>

              <div class="security-note">
                Heslá sa nikdy nenačítavajú späť. Nové heslo zadajte iba vtedy,
                keď ho chcete zmeniť.
              </div>

              <div class="bank-editors">
                <details
                  v-for="bankData in form.bank_data"
                  :key="bankData.bank"
                  :open="bankData.bank === firstConfiguredBankId"
                >
                  <summary>
                    <span class="bank-code">{{ bankMonogram(bankData.bank_name) }}</span>
                    <span>
                      <strong>{{ bankData.bank_name }}</strong>
                      <small>{{ bankConfigurationLabel(bankData) }}</small>
                    </span>
                    <em v-if="isBankConfigured(bankData)">Nastavené</em>
                  </summary>
                  <div class="bank-fields">
                    <div class="fields-grid two">
                      <label class="form-field">
                        <span>ID obchodníka</span>
                        <input v-model.trim="bankData.id_obchodnika" type="text">
                      </label>
                      <label class="form-field">
                        <span>Portálové meno</span>
                        <input v-model.trim="bankData.portal_meno" type="text" autocomplete="off">
                      </label>
                    </div>
                    <div class="fields-grid two">
                      <label class="form-field">
                        <span>Nové portálové heslo</span>
                        <input
                          v-model="bankData.new_portal_password"
                          type="password"
                          autocomplete="new-password"
                          :placeholder="bankData.portal_password_is_set ? 'Heslo je uložené' : 'Nie je nastavené'"
                        >
                      </label>
                      <label class="form-field">
                        <span>Koeficient odmeny (%)</span>
                        <input
                          v-model="bankData.koeficient_odmeny"
                          type="number"
                          min="0"
                          max="999.99"
                          step="0.01"
                        >
                      </label>
                    </div>
                    <label v-if="bankData.portal_password_is_set" class="clear-password">
                      <input v-model="bankData.clear_portal_password" type="checkbox">
                      Vymazať uložené portálové heslo
                    </label>
                  </div>
                </details>
              </div>
            </section>

            <div v-if="formError" class="form-error">{{ formError }}</div>

            <footer class="detail-footer">
              <button class="secondary-button" type="button" :disabled="saving" @click="resetForm">
                {{ creating ? "Vymazať formulár" : "Zahodiť zmeny" }}
              </button>
              <button class="primary-button" type="submit" :disabled="saving">
                {{ saving ? "Ukladám..." : (creating ? "Založiť finančníka" : "Uložiť finančníka") }}
              </button>
            </footer>
          </form>
        </template>
      </section>
    </div>
  </main>
</template>

<script>
import {
  getAdminFinancierMetadata,
  getAdminFinanciers,
  patchAdminFinancier,
  postAdminFinancier,
} from "@/services/brokerService";

const EMPTY_PROFILE = {
  sfa: null,
  telephone: "",
  cislo_registracie_NBS: "",
  obchodne_meno: "",
  adresa: "",
  ico: "",
  email: "",
  fillout_id: "",
  is_pooled: false,
  districts_served: [],
};

export default {
  name: "AdminFinanciersSettings",
  data: () => ({
    financiers: [],
    metadata: {
      districts: [],
      banks: [],
      sfas: [],
    },
    loading: true,
    loadError: false,
    saving: false,
    formError: "",
    search: "",
    districtSearch: "",
    statusFilter: "all",
    selectedId: null,
    creating: false,
    activeTab: "account",
    form: null,
    originalFinancier: null,
    profileImageFile: null,
    profileImagePreview: "",
    statusOptions: [
      {value: "all", label: "Všetci"},
      {value: "active", label: "Aktívni"},
      {value: "inactive", label: "Neaktívni"},
      {value: "pooled", label: "Prijímajú leady"},
    ],
    tabs: [
      {value: "account", label: "Účet"},
      {value: "profile", label: "Profil"},
      {value: "locations", label: "Lokality"},
      {value: "banks", label: "Banky"},
    ],
  }),
  computed: {
    filteredFinanciers () {
      const query = normalizeText(this.search);
      return this.financiers.filter((financier) => {
        if (this.statusFilter === "active" && !financier.is_active) return false;
        if (this.statusFilter === "inactive" && financier.is_active) return false;
        if (
          this.statusFilter === "pooled"
          && !financier.staff_profile?.is_pooled
        ) return false;
        if (!query) return true;
        return normalizeText([
          financier.degree,
          financier.first_name,
          financier.last_name,
          financier.email,
          financier.staff_profile?.obchodne_meno,
        ].join(" ")).includes(query);
      });
    },
    selectedDistricts () {
      if (!this.form) return [];
      const selected = new Set(this.form.profile.districts_served);
      return this.metadata.districts
        .filter((district) => selected.has(district.id))
        .sort((a, b) => a.name.localeCompare(b.name, "sk"));
    },
    selectedDistrictCount () {
      return this.form?.profile.districts_served.length || 0;
    },
    districtGroups () {
      const query = normalizeText(this.districtSearch);
      const groups = new Map();
      this.metadata.districts
        .filter((district) => !query || normalizeText(
          `${district.name} ${district.region_name}`,
        ).includes(query))
        .forEach((district) => {
          const key = district.region_name || "";
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(district);
        });
      return [...groups.entries()].map(([name, districts]) => ({
        name,
        districts,
      }));
    },
    configuredBankCount () {
      return this.form?.bank_data.filter(this.isBankConfigured).length || 0;
    },
    firstConfiguredBankId () {
      return this.form?.bank_data.find(this.isBankConfigured)?.bank
        || this.form?.bank_data[0]?.bank
        || null;
    },
    formAvatarUrl () {
      return this.profileImagePreview || this.form?.profile_image || "";
    },
    formAvatarStyle () {
      return this.formAvatarUrl
        ? {backgroundImage: `url("${this.formAvatarUrl}")`}
        : {};
    },
    formInitials () {
      if (!this.form) return "";
      return `${this.form.first_name?.[0] || ""}${this.form.last_name?.[0] || ""}`.toUpperCase() || "?";
    },
    formDisplayName () {
      if (!this.form) return "";
      const name = [this.form.degree, this.form.first_name, this.form.last_name]
        .filter(Boolean)
        .join(" ");
      return name || "Nový finančník";
    },
  },
  async created () {
    await this.loadData();
  },
  beforeDestroy () {
    this.releaseImagePreview();
  },
  methods: {
    async loadData () {
      this.loading = true;
      this.loadError = false;
      try {
        const [financiers, metadata] = await Promise.all([
          getAdminFinanciers({ordering: "last_name,first_name"}),
          getAdminFinancierMetadata(),
        ]);
        this.metadata = metadata;
        this.financiers = (financiers.results || financiers || [])
          .map((item) => ({...item}));
      } catch (error) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    selectFinancier (financier) {
      this.releaseImagePreview();
      this.selectedId = financier.id;
      this.creating = false;
      this.originalFinancier = clone(financier);
      this.form = this.buildForm(financier);
      this.formError = "";
      this.activeTab = "account";
      this.districtSearch = "";
    },
    startCreating () {
      this.releaseImagePreview();
      this.selectedId = null;
      this.creating = true;
      this.originalFinancier = null;
      this.form = this.buildForm(null);
      this.formError = "";
      this.activeTab = "account";
      this.districtSearch = "";
    },
    closeDetail () {
      this.releaseImagePreview();
      this.form = null;
      this.originalFinancier = null;
      this.selectedId = null;
      this.creating = false;
      this.formError = "";
    },
    resetForm () {
      if (this.creating) {
        this.startCreating();
        return;
      }
      if (this.originalFinancier) {
        this.selectFinancier(this.originalFinancier);
      }
    },
    buildForm (financier) {
      const profile = financier?.staff_profile || EMPTY_PROFILE;
      const existingBankData = new Map(
        (financier?.staff_bank_datas || []).map((item) => [item.bank, item]),
      );
      return {
        id: financier?.id || null,
        degree: financier?.degree || "",
        first_name: financier?.first_name || "",
        last_name: financier?.last_name || "",
        email: financier?.email || "",
        is_active: financier?.is_active ?? true,
        is_temporary: financier?.is_temporary ?? false,
        password: "",
        profile_image: financier?.profile_image || "",
        date_joined: financier?.date_joined || null,
        last_login: financier?.last_login || null,
        profile: {
          ...clone(EMPTY_PROFILE),
          ...clone(profile),
          sfa: profile.sfa || null,
          districts_served: [...(profile.districts_served || [])],
        },
        bank_data: this.metadata.banks.map((bank) => {
          const saved = existingBankData.get(bank.id) || {};
          return {
            id: saved.id || null,
            bank: bank.id,
            bank_code: bank.code,
            bank_name: bank.name,
            id_obchodnika: saved.id_obchodnika || "",
            portal_meno: saved.portal_meno || "",
            portal_password_is_set: Boolean(saved.portal_password_is_set),
            new_portal_password: "",
            clear_portal_password: false,
            koeficient_odmeny: saved.koeficient_odmeny ?? "",
          };
        }),
      };
    },
    async saveFinancier () {
      if (!this.form || this.saving) return;
      const wasCreating = this.creating;
      this.saving = true;
      this.formError = "";
      try {
        const payload = this.buildPayload();
        let saved = this.creating
          ? await postAdminFinancier(payload)
          : await patchAdminFinancier(this.form.id, payload);

        if (this.profileImageFile) {
          const imagePayload = new FormData();
          imagePayload.append("profile_image", this.profileImageFile);
          saved = await patchAdminFinancier(saved.id, imagePayload);
        }

        const index = this.financiers.findIndex((item) => item.id === saved.id);
        if (index >= 0) this.$set(this.financiers, index, saved);
        else this.financiers.push(saved);

        this.selectFinancier(saved);
        this.notify(
          wasCreating
            ? "Finančník bol založený."
            : "Údaje finančníka boli uložené.",
          "success",
        );
      } catch (error) {
        this.formError = apiErrorMessage(error);
        this.notify("Finančníka sa nepodarilo uložiť.", "error");
      } finally {
        this.saving = false;
      }
    },
    buildPayload () {
      const nullable = (value) => value === "" ? null : value;
      const profile = this.form.profile;
      const payload = {
        degree: nullable(this.form.degree),
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        email: this.form.email,
        is_active: Boolean(this.form.is_active),
        is_temporary: Boolean(this.form.is_temporary),
        staff_profile: {
          sfa: profile.sfa || null,
          telephone: nullable(profile.telephone),
          cislo_registracie_NBS: nullable(profile.cislo_registracie_NBS),
          obchodne_meno: nullable(profile.obchodne_meno),
          adresa: nullable(profile.adresa),
          ico: nullable(profile.ico),
          email: nullable(profile.email),
          fillout_id: nullable(profile.fillout_id),
          is_pooled: Boolean(profile.is_pooled),
          districts_served: profile.districts_served,
        },
        staff_bank_datas: this.form.bank_data
          .filter((bankData) => bankData.id || this.isBankConfigured(bankData))
          .map((bankData) => ({
            bank: bankData.bank,
            id_obchodnika: nullable(bankData.id_obchodnika),
            portal_meno: nullable(bankData.portal_meno),
            new_portal_password: bankData.new_portal_password || "",
            clear_portal_password: Boolean(bankData.clear_portal_password),
            koeficient_odmeny: nullable(bankData.koeficient_odmeny),
          })),
      };
      if (this.form.password) payload.password = this.form.password;
      return payload;
    },
    selectProfileImage (event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.releaseImagePreview();
      this.profileImageFile = file;
      this.profileImagePreview = URL.createObjectURL(file);
    },
    releaseImagePreview () {
      if (this.profileImagePreview) URL.revokeObjectURL(this.profileImagePreview);
      this.profileImagePreview = "";
      this.profileImageFile = null;
    },
    toggleDistrict (id) {
      const districts = this.form.profile.districts_served;
      const index = districts.indexOf(id);
      if (index >= 0) districts.splice(index, 1);
      else districts.push(id);
    },
    toggleDistrictGroup (group) {
      const selected = new Set(this.form.profile.districts_served);
      const allSelected = group.districts.every((district) => selected.has(district.id));
      group.districts.forEach((district) => {
        if (allSelected) selected.delete(district.id);
        else selected.add(district.id);
      });
      this.form.profile.districts_served = [...selected];
    },
    isDistrictGroupSelected (group) {
      const selected = new Set(this.form.profile.districts_served);
      return group.districts.length
        && group.districts.every((district) => selected.has(district.id));
    },
    isBankConfigured (bankData) {
      return Boolean(
        bankData.id
        || bankData.id_obchodnika
        || bankData.portal_meno
        || bankData.portal_password_is_set
        || bankData.new_portal_password
        || bankData.koeficient_odmeny !== "",
      );
    },
    bankConfigurationLabel (bankData) {
      const fields = [
        bankData.id_obchodnika && "ID obchodníka",
        bankData.portal_meno && "portál",
        (bankData.portal_password_is_set || bankData.new_portal_password) && "heslo",
        bankData.koeficient_odmeny !== "" && "odmena",
      ].filter(Boolean);
      return fields.length ? fields.join(" · ") : "Bez údajov";
    },
    avatarStyle (financier) {
      return financier.profile_image
        ? {backgroundImage: `url("${financier.profile_image}")`}
        : {};
    },
    fullName (financier) {
      return [financier.degree, financier.first_name, financier.last_name]
        .filter(Boolean)
        .join(" ") || financier.email;
    },
    initials (financier) {
      return `${financier.first_name?.[0] || ""}${financier.last_name?.[0] || ""}`.toUpperCase() || "?";
    },
    bankMonogram (name) {
      return String(name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },
    financierCountLabel (count) {
      if (count === 1) return "finančník";
      if (count >= 2 && count <= 4) return "finančníci";
      return "finančníkov";
    },
    formatDate (value) {
      if (!value) return "";
      return new Intl.DateTimeFormat("sk-SK", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value));
    },
    notify (text, type) {
      this.$store.commit("appState/setSuccessMessage", {text, type});
    },
  },
};

function clone (value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeText (value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function apiErrorMessage (error) {
  const data = error?.response?.data;
  if (!data) return "Skontrolujte pripojenie a skúste to znova.";
  if (typeof data === "string") return data;

  const messages = [];
  const collect = (value, prefix = "") => {
    if (Array.isArray(value)) {
      value.forEach((item) => collect(item, prefix));
    } else if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, item]) => {
        collect(item, prefix ? `${prefix} / ${key}` : key);
      });
    } else if (value) {
      messages.push(`${prefix ? `${prefix}: ` : ""}${value}`);
    }
  };
  collect(data);
  return messages.join(" ") || "Údaje sa nepodarilo uložiť.";
}
</script>

<style scoped lang="scss">
.financiers-card {
  overflow: hidden;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 24px;
  background: var(--app-surface, #fff);
  color: var(--app-text, #0a1953);
  box-shadow: var(--app-shadow, 0 8px 24px rgba(10, 25, 83, 0.06));
}

.financiers-heading,
.detail-heading,
.detail-identity,
.form-section-heading,
.detail-footer,
.setting-switch,
.avatar-editor {
  display: flex;
  align-items: center;
}

.financiers-heading {
  justify-content: space-between;
  gap: 20px;
  padding: 26px 28px 22px;
  border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
}

.section-kicker,
.detail-eyebrow {
  color: var(--app-primary, #2d7dd2);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.financiers-heading h2 {
  margin: 3px 0 5px;
  font-size: 24px;
}

.financiers-heading p,
.form-section-heading p {
  margin: 0;
  color: var(--app-text-muted, #6f7f96);
  font-size: 11px;
}

.primary-button,
.secondary-button {
  min-height: 40px;
  padding: 0 15px;
  border: 0;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}

.primary-button {
  background: linear-gradient(135deg, var(--app-primary, #2d7dd2), #1854a8);
  color: #fff;
  box-shadow: 0 8px 18px rgba(45, 125, 210, 0.2);
}

.secondary-button {
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  background: var(--app-surface-muted, #f4f8fc);
  color: var(--app-text, #0a1953);
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.financiers-workspace {
  display: grid;
  min-height: 720px;
  grid-template-columns: 310px minmax(0, 1fr);
}

.financiers-list {
  border-right: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  background: var(--app-surface-muted, #f4f8fc);
}

.list-toolbar {
  padding: 17px;
  border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
}

.list-toolbar label,
.form-field,
.district-search {
  display: grid;
  gap: 6px;
}

.list-toolbar label > span,
.form-field > span,
.district-search > span {
  color: var(--app-text-muted, #6f7f96);
  font-size: 9px;
  font-weight: 800;
}

.list-toolbar input,
.form-field input,
.form-field select,
.district-search input {
  width: 100%;
  height: 42px;
  padding: 0 11px;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 10px;
  background: var(--app-surface, #fff);
  color: var(--app-text, #0a1953);
  font: inherit;
  font-size: 11px;
  outline: none;
}

.list-toolbar input:focus,
.form-field input:focus,
.form-field select:focus,
.district-search input:focus {
  border-color: var(--app-primary, #2d7dd2);
  box-shadow: 0 0 0 3px var(--app-primary-soft, rgba(45, 125, 210, 0.12));
}

.status-filter {
  display: flex;
  gap: 5px;
  margin-top: 10px;
  overflow-x: auto;
}

.status-filter button {
  flex: 0 0 auto;
  padding: 6px 8px;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 999px;
  background: var(--app-surface, #fff);
  color: var(--app-text-muted, #6f7f96);
  font-size: 8px;
  font-weight: 800;
  cursor: pointer;
}

.status-filter button.active {
  border-color: var(--app-primary, #2d7dd2);
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
}

.list-count {
  padding: 11px 17px 6px;
  color: var(--app-text-muted, #6f7f96);
  font-size: 9px;
  font-weight: 700;
}

.financier-items {
  max-height: 640px;
  padding: 5px 10px 18px;
  overflow-y: auto;
}

.financier-item {
  display: grid;
  width: 100%;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 11px;
  border: 1px solid transparent;
  border-radius: 13px;
  background: transparent;
  color: var(--app-text, #0a1953);
  text-align: left;
  cursor: pointer;
}

.financier-item:hover,
.financier-item.active {
  border-color: var(--app-border, rgba(45, 125, 210, 0.16));
  background: var(--app-surface, #fff);
  box-shadow: 0 5px 14px rgba(10, 25, 83, 0.05);
}

.financier-item.active {
  border-color: var(--app-primary, #2d7dd2);
}

.avatar {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  background-color: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  background-position: center;
  background-size: cover;
  color: var(--app-primary-hover, #2464a8);
  font-size: 11px;
  font-weight: 900;
}

.avatar.inactive {
  filter: grayscale(1);
  opacity: 0.6;
}

.avatar.large {
  width: 52px;
  height: 52px;
  border-radius: 15px;
  font-size: 13px;
}

.avatar.xlarge {
  flex: 0 0 74px;
  width: 74px;
  height: 74px;
  border-radius: 20px;
  font-size: 18px;
}

.financier-summary {
  min-width: 0;
}

.financier-summary strong,
.financier-summary small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.financier-summary strong {
  font-size: 11px;
}

.financier-summary small {
  margin-top: 2px;
  color: var(--app-text-muted, #6f7f96);
  font-size: 8px;
}

.summary-meta {
  display: flex;
  gap: 4px;
  margin-top: 5px;
}

.summary-meta em {
  padding: 3px 5px;
  border-radius: 999px;
  background: var(--app-surface-strong, #dceafb);
  color: var(--app-text-muted, #6f7f96);
  font-size: 7px;
  font-style: normal;
  font-weight: 800;
}

.summary-meta em.success {
  background: var(--app-success-soft, #edf7f2);
  color: var(--app-success, #24754c);
}

.summary-meta em.pooling {
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
}

.client-count {
  color: var(--app-text-muted, #6f7f96);
  text-align: center;
}

.client-count strong,
.client-count small {
  display: block;
}

.client-count strong {
  color: var(--app-text, #0a1953);
  font-size: 11px;
}

.client-count small {
  font-size: 7px;
}

.empty-list {
  padding: 30px 12px;
  color: var(--app-text-muted, #6f7f96);
  font-size: 10px;
  text-align: center;
}

.financier-detail {
  min-width: 0;
  background: var(--app-surface, #fff);
}

.empty-detail,
.financiers-state {
  display: flex;
  min-height: 600px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 30px;
  color: var(--app-text-muted, #6f7f96);
  text-align: center;
}

.empty-detail-icon {
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  border-radius: 17px;
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  font-size: 22px;
}

.empty-detail strong {
  color: var(--app-text, #0a1953);
  font-size: 15px;
}

.empty-detail p {
  max-width: 320px;
  margin: 0 0 4px;
  font-size: 10px;
}

.detail-heading {
  justify-content: space-between;
  gap: 15px;
  padding: 18px 22px;
  border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
}

.detail-identity {
  min-width: 0;
  gap: 12px;
}

.detail-identity h3 {
  margin: 2px 0;
  overflow: hidden;
  font-size: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-identity > div > span {
  color: var(--app-text-muted, #6f7f96);
  font-size: 9px;
}

.close-detail {
  width: 34px;
  height: 34px;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 10px;
  background: var(--app-surface-muted, #f4f8fc);
  color: var(--app-text-muted, #6f7f96);
  font-size: 20px;
  cursor: pointer;
}

.detail-tabs {
  display: flex;
  gap: 4px;
  padding: 10px 18px;
  border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  overflow-x: auto;
}

.detail-tabs button {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  padding: 8px 11px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--app-text-muted, #6f7f96);
  font-size: 9px;
  font-weight: 800;
  cursor: pointer;
}

.detail-tabs button.active {
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
}

.detail-tabs button span {
  display: grid;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  place-items: center;
  border-radius: 999px;
  background: var(--app-surface, #fff);
  font-size: 7px;
}

.detail-form {
  position: relative;
}

.form-section {
  min-height: 510px;
  padding: 24px;
}

.form-section-heading {
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.form-section-heading h4 {
  margin: 0 0 4px;
  font-size: 15px;
}

.fields-grid {
  display: grid;
  gap: 13px;
  margin-bottom: 13px;
}

.fields-grid.two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.fields-grid.three {
  grid-template-columns: 0.45fr 1fr 1fr;
}

.form-field {
  margin-bottom: 13px;
}

.fields-grid .form-field {
  margin-bottom: 0;
}

.avatar-editor {
  gap: 14px;
  margin-bottom: 22px;
  padding: 14px;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 14px;
  background: var(--app-surface-muted, #f4f8fc);
}

.file-button {
  position: relative;
  overflow: hidden;
  padding: 9px 11px;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 9px;
  background: var(--app-surface, #fff);
  color: var(--app-primary-hover, #2464a8);
  font-size: 9px;
  font-weight: 800;
  cursor: pointer;
}

.file-button input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.avatar-editor > small {
  color: var(--app-text-muted, #6f7f96);
  font-size: 8px;
}

.switch-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0;
}

.setting-switch {
  position: relative;
  gap: 11px;
  padding: 13px;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 13px;
  background: var(--app-surface-muted, #f4f8fc);
  cursor: pointer;
}

.setting-switch.featured {
  margin-top: 19px;
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
}

.setting-switch > input {
  position: absolute;
  opacity: 0;
}

.setting-switch strong,
.setting-switch small {
  display: block;
}

.setting-switch strong {
  font-size: 10px;
}

.setting-switch small {
  margin-top: 2px;
  color: var(--app-text-muted, #6f7f96);
  font-size: 8px;
  line-height: 1.4;
}

.switch-track {
  position: relative;
  flex: 0 0 36px;
  width: 36px;
  height: 21px;
  border-radius: 999px;
  background: #a7b4c5;
  transition: background 0.2s ease;
}

.switch-track > span {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 5px rgba(10, 25, 83, 0.2);
  transition: transform 0.2s ease;
}

.setting-switch > input:checked + .switch-track {
  background: var(--app-success, #24754c);
}

.setting-switch > input:checked + .switch-track > span {
  transform: translateX(15px);
}

.read-only-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
  margin-top: 20px;
}

.read-only-meta > div {
  padding: 11px;
  border-radius: 10px;
  background: var(--app-surface-muted, #f4f8fc);
}

.read-only-meta span,
.read-only-meta strong {
  display: block;
}

.read-only-meta span {
  margin-bottom: 3px;
  color: var(--app-text-muted, #6f7f96);
  font-size: 8px;
}

.read-only-meta strong {
  font-size: 9px;
}

.selection-count {
  flex: 0 0 auto;
  padding: 7px 9px;
  border-radius: 999px;
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
  font-size: 8px;
  font-weight: 800;
}

.selected-districts {
  display: flex;
  gap: 6px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.selected-districts button {
  padding: 6px 8px;
  border: 0;
  border-radius: 999px;
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
  font-size: 8px;
  font-weight: 700;
  cursor: pointer;
}

.selected-districts button span {
  margin-left: 3px;
}

.district-search {
  margin-bottom: 15px;
}

.district-groups {
  max-height: 380px;
  padding-right: 7px;
  overflow-y: auto;
}

.district-groups > section {
  margin-bottom: 12px;
  overflow: hidden;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 12px;
}

.district-group-heading {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  background: var(--app-surface-muted, #f4f8fc);
}

.district-group-heading strong {
  font-size: 9px;
}

.district-group-heading button {
  border: 0;
  background: transparent;
  color: var(--app-primary, #2d7dd2);
  font-size: 8px;
  font-weight: 800;
  cursor: pointer;
}

.district-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  background: var(--app-border, rgba(45, 125, 210, 0.12));
}

.district-options label {
  display: flex;
  min-height: 35px;
  gap: 7px;
  align-items: center;
  padding: 8px 10px;
  background: var(--app-surface, #fff);
  font-size: 8px;
  cursor: pointer;
}

.district-options input {
  accent-color: var(--app-primary, #2d7dd2);
}

.security-note {
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid rgba(166, 111, 0, 0.18);
  border-radius: 10px;
  background: #fff8e8;
  color: #7b5700;
  font-size: 9px;
  font-weight: 600;
}

.bank-editors {
  display: grid;
  gap: 9px;
}

.bank-editors details {
  overflow: hidden;
  border: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  border-radius: 12px;
  background: var(--app-surface, #fff);
}

.bank-editors summary {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 11px 13px;
  cursor: pointer;
  list-style: none;
}

.bank-editors summary::-webkit-details-marker {
  display: none;
}

.bank-code {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 10px;
  background: var(--app-primary-soft, rgba(45, 125, 210, 0.12));
  color: var(--app-primary-hover, #2464a8);
  font-size: 9px;
  font-weight: 900;
}

.bank-editors summary strong,
.bank-editors summary small {
  display: block;
}

.bank-editors summary strong {
  font-size: 10px;
}

.bank-editors summary small {
  margin-top: 2px;
  color: var(--app-text-muted, #6f7f96);
  font-size: 8px;
}

.bank-editors summary em {
  padding: 5px 7px;
  border-radius: 999px;
  background: var(--app-success-soft, #edf7f2);
  color: var(--app-success, #24754c);
  font-size: 7px;
  font-style: normal;
  font-weight: 800;
}

.bank-fields {
  padding: 14px;
  border-top: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  background: var(--app-surface-muted, #f4f8fc);
}

.clear-password {
  display: flex;
  gap: 7px;
  align-items: center;
  color: var(--app-danger, #a63f3f);
  font-size: 8px;
  font-weight: 700;
}

.clear-password input {
  accent-color: var(--app-danger, #a63f3f);
}

.form-error {
  margin: 0 24px 14px;
  padding: 11px 13px;
  border: 1px solid rgba(166, 63, 63, 0.18);
  border-radius: 10px;
  background: var(--app-danger-soft, #fff1f1);
  color: var(--app-danger, #a63f3f);
  font-size: 9px;
  font-weight: 700;
  line-height: 1.5;
}

.detail-footer {
  position: sticky;
  bottom: 0;
  justify-content: flex-end;
  gap: 9px;
  padding: 13px 22px;
  border-top: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  background: color-mix(in srgb, var(--app-surface, #fff) 94%, transparent);
  backdrop-filter: blur(12px);
}

@media screen and (max-width: 1050px) {
  .financiers-workspace {
    grid-template-columns: 260px minmax(0, 1fr);
  }

  .district-options {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 800px) {
  .financiers-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .financiers-heading .primary-button {
    width: 100%;
  }

  .financiers-workspace {
    grid-template-columns: 1fr;
  }

  .financiers-list {
    border-right: 0;
    border-bottom: 1px solid var(--app-border, rgba(45, 125, 210, 0.16));
  }

  .financier-items {
    display: grid;
    max-height: none;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 7px;
    overflow-x: visible;
  }

  .financier-item {
    min-width: 0;
  }

  .fields-grid.two,
  .fields-grid.three,
  .switch-grid {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 520px) {
  .financiers-heading,
  .form-section {
    padding-right: 16px;
    padding-left: 16px;
  }

  .detail-heading {
    padding-right: 14px;
    padding-left: 14px;
  }

  .district-options,
  .read-only-meta {
    grid-template-columns: 1fr;
  }

  .avatar-editor {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
